import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, UsersThree, FileMagnifyingGlass, CheckCircle, Warning, CheckFat } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PdfViewerModal } from "../components/PdfViewerModal";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { downloadAllAuditPackets, saveGeneratedInApp } from "../lib/auditDownload";

const FIELDS = [
  { field: "Member", req: true, desc: "Client name (LAST FIRST) — matched to your client list" },
  { field: "Member ID", req: true, desc: "Client ID (e.g. TOV-900091) — matched to client code, more reliable than name" },
  { field: "DCW", req: true, desc: "Caregiver name — matched to your staff roster" },
  { field: "DCW Code", req: true, desc: "Caregiver ID (e.g. TOV-1008) — authoritative match to your Employee/DCW codes" },
  { field: "Visit ID", req: true, desc: "Unique visit number — re-importing the same file skips duplicates automatically" },
  { field: "Visit Date", req: true, desc: "Date of service (e.g. 1/1/2019)" },
  { field: "Scheduled Start / End", req: false, desc: "Planned times (e.g. 900 / 1200) — scheduled hours" },
  { field: "Visit Start / End", req: false, desc: "Actual EVV punch times — worked hours" },
  { field: "Duration", req: false, desc: "Cross-check, used when punch times are missing" },
  { field: "Missed Visit", req: false, desc: "Yes = missed visit — excluded from worked-since reports" },
  { field: "MCO", req: false, desc: "Payer, e.g. KEYSTONE FIRST CHC (TOV)" },
  { field: "Service Code / Discipline", req: false, desc: "e.g. W1793 / PCA" },
  { field: "Billed / Invoice Number", req: false, desc: "Billing status and invoice reference" },
  { field: "Coordinator / County", req: false, desc: "Stored with the visit for context" },
];

const FLOW = [
  { icon: DownloadSimple, title: "1. Upload your HHAeXchange export", text: "The importer is matched to your real export columns (below) — Member/Member ID, DCW/DCW Code, Visit ID, times, payer. The first run is always a preview: nothing is written until you've reviewed the match report." },
  { icon: FileMagnifyingGlass, title: "2. Review the preview, then import", text: "Preview shows rows recognized, date range, missed visits, and any caregiver/client names that don't match the roster. Then uncheck “Preview only” and import — duplicates are skipped by Visit ID, so re-runs are safe." },
  { icon: UsersThree, title: "3. Audit-day reverse lookup", text: "Surveyor hands you a client list → you pick the client → instantly see every caregiver who worked with them → open each one's Evidence Packet. That's the whole audit pull, in minutes." },
];

export default function AuditPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [readiness, setReadiness] = useState(null);
  const [showClear, setShowClear] = useState(false);
  const [genFiles, setGenFiles] = useState([]);

  useEffect(() => {
    if (isAdmin) api.get("/audit/staff-readiness").then((r) => setReadiness(r.data)).catch(() => setReadiness({ items: [] }));
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) api.get("/admin/generated-list").then((r) => setGenFiles(r.data.files || [])).catch(() => {});
  }, [isAdmin]);

  const [visits, setVisits] = useState(null);
  const [visitClients, setVisitClients] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [evSuggestions, setEvSuggestions] = useState(null);
  const [evStaff, setEvStaff] = useState([]);
  const [evAssign, setEvAssign] = useState({});
  const [evAlias, setEvAlias] = useState({});
  const [evGroups, setEvGroups] = useState([]);
  const [showFin, setShowFin] = useState(false);
  useEffect(() => { api.get("/settings/features").then((r) => setShowFin(!!r.data.financials)).catch(() => {}); }, []);
  const [sinceDate, setSinceDate] = useState("2024-09-15");
  const [whoWorked, setWhoWorked] = useState(null);
  const [whoBusy, setWhoBusy] = useState(false);

  const runWhoWorked = async () => {
    if (!sinceDate) { toast.error("Pick a date first"); return; }
    setWhoBusy(true);
    try {
      const { data } = await api.get(`/audit/who-worked?since=${sinceDate}`);
      setWhoWorked(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWhoBusy(false);
    }
  };

  const [fin, setFin] = useState(null);
  const [finBusy, setFinBusy] = useState(false);
  const [finTab, setFinTab] = useState("clients");
  const [burden, setBurden] = useState("12");
  const [chcRate, setChcRate] = useState("21.57");

  const loadFin = async () => {
    setFinBusy(true);
    try {
      const [{ data: s }, { data: m }] = await Promise.all([
        api.get("/finance/settings"),
        api.get("/finance/margins?since=2024-09-15"),
      ]);
      setFin(m);
      setBurden(String(s.burden_pct));
      setChcRate(String(s.chc_bill_rate));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFinBusy(false);
    }
  };

  const saveFinSettings = async () => {
    try {
      await api.put("/finance/settings", { burden_pct: parseFloat(burden) || 0, chc_bill_rate: parseFloat(chcRate) || 0 });
      toast.success("Financial settings saved");
      loadFin();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [keyFile, setKeyFile] = useState(null);
  const [keyInfo, setKeyInfo] = useState(null);
  const [keyBusy, setKeyBusy] = useState(false);
  const [keyResult, setKeyResult] = useState(null);

  const loadKeyInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/name-key");
      setKeyInfo(data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { if (isAdmin) { loadKeyInfo(); loadFin(); } }, [isAdmin, loadKeyInfo]);

  const uploadKey = async () => {
    if (!keyFile) return;
    setKeyBusy(true);
    const fd = new FormData();
    fd.append("file", keyFile);
    try {
      const { data } = await api.post("/name-key/import", fd);
      toast.success(`${data.count} key entries saved`);
      setKeyFile(null);
      setKeyResult(null);
      loadKeyInfo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setKeyBusy(false);
    }
  };

  const applyKey = async () => {
    if (!window.confirm("Rewrite every masked client name to its real name across all visits and client records? This cannot be undone.")) return;
    setKeyBusy(true);
    try {
      const { data } = await api.post("/name-key/apply");
      setKeyResult(data);
      toast.success(`Re-identified ${data.clients_updated} clients and ${data.visits_updated} visits`);
      loadKeyInfo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setKeyBusy(false);
    }
  };

  const loadVisits = useCallback(() => {
    if (!isAdmin) return;
    api.get("/visits/summary").then((r) => setVisits(r.data)).catch(() => {});
    api.get("/audit/visit-clients").then((r) => setVisitClients(r.data.items || [])).catch(() => {});
    api.get("/interviews/records").then((r) => setInterviewRecs(r.data.items || [])).catch(() => {});
    api.get("/compliance/documents").then((r) => setRollup(r.data.items || [])).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { loadVisits(); }, [loadVisits]);

  const [interviewRecs, setInterviewRecs] = useState(null);
  const [ivSearch, setIvSearch] = useState("");
  const [rollup, setRollup] = useState(null);
  const [rollupOpen, setRollupOpen] = useState("");
  const [remindBusy, setRemindBusy] = useState("");

  const remindItem = async (item) => {
    setRemindBusy(item.key);
    try {
      const { data } = await api.post("/compliance/documents/remind", { key: item.key });
      toast.success(`Reminders for "${item.label}": ${data.queued} queued for approval, ${data.sent} sent, ${data.skipped} skipped (already reminded today or rule off)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemindBusy("");
    }
  };

  const uploadVisits = async () => {
    if (!uploadFile) { toast.error("Choose the visit log file first"); return; }
    setUploadBusy(true);
    setUploadResult(null);
    setUploadProgress({ phase: "uploading", done: 0, total: 1 });
    try {
      // Always chunked: a single 4 MB request ceiling bypasses proxy body-size limits on any file size
      const CHUNK = 4 * 1024 * 1024;
      const { data: start } = await api.post("/visits/import/start", {});
      const total = Math.max(1, Math.ceil(uploadFile.size / CHUNK));
      for (let i = 0; i < total; i++) {
        const blob = uploadFile.slice(i * CHUNK, (i + 1) * CHUNK);
        await api.post(`/visits/import/chunk/${start.upload_id}`, blob, {
          headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
        });
        setUploadProgress({ phase: "uploading", done: i + 1, total });
      }
      setUploadProgress({ phase: "processing", done: total, total });
      const { data } = await api.post("/visits/import/finish",
        { upload_id: start.upload_id, filename: uploadFile.name, dry_run: dryRun }, { timeout: 600000 });
      setUploadResult(data);
      toast.success(data.dry_run
        ? `Preview: ${data.rows} rows recognized — nothing written yet`
        : `Imported ${data.inserted} visits (${data.duplicates} duplicates skipped)`);
      setUploadFile(null);
      if (!data.dry_run) loadVisits();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadBusy(false);
      setUploadProgress(null);
    }
  };

  const openClientLookup = async (name) => {
    setLookupBusy(true);
    setLookup(null);
    try {
      const { data } = await api.get(`/audit/visit-clients/${encodeURIComponent(name)}/caregivers`);
      setLookup(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLookupBusy(false);
    }
  };

  const [ivFile, setIvFile] = useState(null);
  const [ivBusy2, setIvBusy2] = useState(false);
  const [ivResult, setIvResult] = useState(null);

  const [resetPreview, setResetPreview] = useState(null);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [pdfView, setPdfView] = useState(null);

  const doSeed = async () => {
    setSeedBusy(true);
    try {
      const { data } = await api.post("/admin/seed-baseline", {}, { timeout: 600000 });
      setSeedResult(data);
      if (data.ok) toast.success(`Baseline seeded — ${data.staff_accounts} staff accounts`);
      else toast.error("Seed finished with errors — see step details below");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeedBusy(false);
    }
  };
  const [pack, setPack] = useState({ state: "idle", done: 0, total: 0 });
  const [packBusy, setPackBusy] = useState(false);

  const pollPack = async () => {
    try {
      const { data } = await api.get("/audit-packs/status");
      setPack(data);
      return data;
    } catch { return null; }
  };
  useEffect(() => { pollPack(); }, []);
  useEffect(() => {
    if (pack.state !== "running") return;
    const t = setInterval(pollPack, 5000);
    return () => clearInterval(t);
  }, [pack.state]);

  const buildPack = async () => {
    setPackBusy(true);
    try {
      await api.post("/audit-packs/build");
      toast.success("Audit pack build started — this runs in the background");
      pollPack();
    } catch (e) { toast.error(formatApiError(e)); }
    setPackBusy(false);
  };

  const downloadPack = async () => {
    try {
      const r = await api.get("/audit-packs/download", { responseType: "blob", timeout: 600000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "Silver_Home_Care_Audit_Pack.zip";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [evZip, setEvZip] = useState(null);
  const [evBusy, setEvBusy] = useState(false);
  const [evResult, setEvResult] = useState(null);

  const loadResetPreview = async () => {
    try {
      const { data } = await api.get("/admin/factory-reset/preview");
      setResetPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doFactoryReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/factory-reset", { confirm: resetConfirm }, { timeout: 300000 });
      setResetResult(data);
      setResetPreview(null);
      setResetConfirm("");
      toast.success(`Reset complete — ${data.staff_accounts_deleted} staff accounts removed`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  const doBulkImport = async () => {
    if (!evZip) { toast.error("Choose the zip of evidence PDFs first"); return; }
    setEvBusy(true);
    setEvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", evZip);
      const { data } = await api.post("/training-evidence/bulk-import", fd, { timeout: 300000 });
      setEvResult(data);
      toast.success(`Imported ${data.imported} evidence files (${data.skipped_duplicates} duplicates skipped)`);
      if (data.unmatched_names?.length) {
        const [{ data: sug }, { data: staff }] = await Promise.all([
          api.post("/training-evidence/suggest-matches", { names: data.unmatched_names }),
          api.get("/users"),
        ]);
        setEvSuggestions(sug.suggestions || []);
        const rows = Array.isArray(staff) ? staff : (staff.users || staff.staff || []);
        setEvStaff(rows.map((u) => ({ id: u.id || u._id, name: u.name })).filter((s) => s.id && s.name));
        const groups = {};
        for (const fn of data.unmatched_names) {
          const s = (sug.suggestions || []).find((x) => x.filename === fn);
          const key = s?.name_guess || fn;
          (groups[key] = groups[key] || { name: key, files: [], candidates: s?.candidates || [] }).files.push(fn);
        }
        setEvGroups(Object.values(groups));
        setEvAssign({});
        setEvAlias({});
      } else {
        setEvSuggestions(null);
        setEvZip(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEvBusy(false);
    }
  };

  const attachAssigned = async () => {
    const entries = Object.entries(evAssign).filter(([, uid]) => uid);
    if (!entries.length || !evZip) { toast.error("Pick a person for at least one unmatched name"); return; }
    setEvBusy(true);
    try {
      const assignments = {};
      for (const [gname, uid] of entries) {
        const g = evGroups.find((x) => x.name === gname);
        if (!g) continue;
        if (evAlias[gname]) {
          const { data: det } = await api.get(`/users/${uid}`);
          const cur = det.profile?.aliases || [];
          if (!cur.includes(gname)) await api.put(`/users/${uid}/profile`, { aliases: [...cur, gname] });
        }
        for (const fn of g.files) assignments[fn] = uid;
      }
      const fd = new FormData();
      fd.append("file", evZip);
      fd.append("assignments", JSON.stringify(assignments));
      const { data } = await api.post("/training-evidence/bulk-import", fd, { timeout: 300000 });
      toast.success(`Attached ${data.imported} file(s) to assigned staff`);
      setEvResult(data);
      setEvSuggestions(null);
      setEvGroups([]);
      setEvZip(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEvBusy(false);
    }
  };

  const uploadInterviews = async () => {
    if (!ivFile) { toast.error("Choose the Airtable interview notes CSV first"); return; }
    setIvBusy2(true);
    setIvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", ivFile);
      const { data } = await api.post("/interviews/import", fd, { timeout: 180000 });
      setIvResult(data);
      toast.success(`Imported ${data.created} interview records (${data.skipped_existing} already on file)`);
      setIvFile(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIvBusy2(false);
    }
  };

  const [health, setHealth] = useState(null);
  useEffect(() => { api.get("/admin/data-health").then((r) => setHealth(r.data)).catch(() => {}); }, []);

  const [compFile, setCompFile] = useState(null);
  const [compBusy, setCompBusy] = useState(false);
  const [compResult, setCompResult] = useState(null);
  const importCompetency = async () => {
    if (!compFile) return;
    setCompBusy(true); setCompResult(null);
    try {
      const fd = new FormData();
      fd.append("file", compFile);
      const { data } = await api.post("/competency/import-zip", fd, { timeout: 300000 });
      setCompResult(data);
      toast.success(`Imported ${data.created} competency documents`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setCompBusy(false); }
  };

  const doBundledIvImport = async () => {
    setIvBusy2(true);
    setIvResult(null);
    try {
      const { data } = await api.post("/interviews/import-bundled", {}, { timeout: 300000 });
      setIvResult(data);
      toast.success(`Imported ${data.created} interview records (${data.skipped_existing} already on file)`);
      loadVisits();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIvBusy2(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="audit-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Audit Prep</h1>
      <p className="mt-1 max-w-2xl text-sm text-stone-500">
        When surveyors arrive, they pick clients and ask for the records of everyone who worked with them.
        This page turns your visit data into that answer — starting with confirming the upload format.
      </p>

      <div className="mt-8 space-y-4">
        {FLOW.map((s, i) => (
          <div key={s.title} className="flex gap-4 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`audit-flow-${i}`}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
              <s.icon size={20} />
            </span>
            <div>
              <p className="font-heading text-sm font-semibold text-stone-900">{s.title}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-stone-500">{s.text}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="audit-field-spec">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-stone-800">Visit upload format (draft — confirm before we wire the import)</p>
            <p className="mt-0.5 text-xs text-stone-400">13 columns. The mock file has 3 example rows so you can see exactly what each column means.</p>
          </div>
          {isAdmin && (
            <a href={`${api.defaults.baseURL}/visits/template`} download
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-stone-900"
              data-testid="audit-template-download">
              <DownloadSimple size={15} /> Download mock CSV
            </a>
          )}
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-stone-200 text-[10px] font-bold uppercase tracking-wide text-stone-400">
                <th className="pb-2 pr-4">Column</th>
                <th className="pb-2 pr-4">Required</th>
                <th className="pb-2">What it is / how it's used</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => (
                <tr key={f.field} className="border-b border-stone-100 last:border-0" data-testid={`audit-field-${f.field}`}>
                  <td className="py-2 pr-4 font-mono font-semibold text-stone-700">{f.field}</td>
                  <td className="py-2 pr-4">
                    {f.req
                      ? <span className="rounded bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#C0392B]">REQUIRED</span>
                      : <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-400">optional</span>}
                  </td>
                  <td className="py-2 text-stone-600">{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {health && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="data-health-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-stone-800">Data health — live counts</p>
            <button onClick={downloadAllAuditPackets}
              className="inline-flex h-8 items-center rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
              data-testid="bulk-packets-download">Download all audit packets (ZIP)</button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["Staff", health.staff_total, `${health.staff_active} active`, "health-staff", "/users"],
              ["Interview records", health.interview_records, `${health.staff_with_interview} staff covered`, "health-interviews", "/hiring"],
              ["References", health.references_total, `${health.references_satisfactory} satisfactory · ${health.references_pending} pending`, "health-references", "/references"],
              ["Competency records", health.competency_records, "exams + certificates", "health-competency", "/training"],
              ["IDs on file", health.ids_on_file, `of ${health.staff_total} staff`, "health-ids", "/users"],
              ["Knowledge sources", health.kb_sources, `${health.kb_approved} approved`, "health-kb", "/knowledge"],
              ["Policies", health.policies, "in library", "health-policies", "/policies"],
              ["CAPs", health.caps_total, `${health.caps_verified} verified · ${health.caps_overdue} overdue`, "health-caps", "/quality"]].map(([label, val, sub, tid, to]) => (
              <Link key={tid} to={to} className="rounded-md border border-stone-100 bg-stone-50 p-3 transition-colors hover:border-primary/40 hover:bg-white" data-testid={tid} title={`Open ${label.toLowerCase()} evidence`}>
                <p className="font-heading text-xl font-bold text-stone-900">{val}</p>
                <p className="text-[11px] font-medium text-stone-600">{label} →</p>
                <p className="text-[10px] text-stone-400">{sub}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {isAdmin && genFiles.length > 0 && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="generated-files-card">
          <p className="text-sm font-semibold text-stone-800">Generated packets &amp; reports — download here in the app</p>
          <p className="mt-0.5 text-xs text-stone-500">Click a file — it saves through the app (works even when direct browser downloads get blocked).</p>
          <div className="mt-3 space-y-1.5">
            {genFiles.map((f) => (
              <div key={f.name} className="flex items-center justify-between gap-3 rounded-md border border-stone-100 bg-stone-50 px-3 py-2" data-testid={`genfile-${f.name}`}>
                <p className="truncate text-xs font-medium text-stone-700">{f.name}
                  <span className="ml-2 text-[10px] font-normal text-stone-400">{f.size >= 1048576 ? `${(f.size / 1048576).toFixed(0)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}</span>
                </p>
                <button onClick={() => saveGeneratedInApp(f.name)}
                  className="shrink-0 rounded-md border border-stone-300 px-3 py-1 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white"
                  data-testid={`genfile-dl-${f.name}`}>Save</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-8 rounded-lg border border-primary/30 bg-primary/5 p-5" data-testid="uploads-moved-card">
          <p className="text-sm font-semibold text-stone-800">All uploads now live on the <a href="/uploads" className="font-bold text-primary underline" data-testid="uploads-moved-link">Uploads page</a> (top menu)</p>
          <p className="mt-0.5 text-xs text-stone-500">Visit log · training evidence ZIPs · competency scans · interview records · employee documents · roster/client CSVs · EVV numbers · missed punch forms · name key — all in one place, with the "who's missing what" board.</p>
        </div>
      )}

      {isAdmin && (
        <div className="mt-8 rounded-lg border-2 border-[#C0392B]/40 bg-white p-5 shadow-sm" data-testid="factory-reset-card">
          <p className="text-sm font-semibold text-[#C0392B]">Factory reset — start over with a clean roster</p>
          <p className="mt-0.5 text-xs text-stone-500">
            Permanently deletes ALL staff accounts (except admins), clients &amp; care plans, and every record — training evidence, completions, interviews, TB screenings, employee documents, visits, notes. This cannot be undone.
          </p>
          <p className="mt-1 text-xs text-stone-500">
            <strong>Kept:</strong> admin logins, policies, knowledge base, training catalog &amp; quizzes, orientation curriculum, guidelines, letter templates, settings.
          </p>
          {!resetPreview && !resetResult && (
            <Button onClick={loadResetPreview} variant="outline" className="mt-3 border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/5" data-testid="factory-reset-preview-button">
              Review what will be deleted…
            </Button>
          )}
          {resetPreview && (
            <div className="mt-3 rounded-md border border-[#C0392B]/30 bg-[#C0392B]/5 p-3 text-xs text-stone-700" data-testid="factory-reset-preview">
              <p className="font-semibold" data-testid="factory-reset-summary">
                Will delete: {resetPreview.staff_accounts} staff accounts · {resetPreview.collections.clients || 0} clients · {resetPreview.collections.training_completions || 0} training completions · {resetPreview.collections.screening_completions || 0} screening records · {resetPreview.collections.inbox_pool || 0} inbox files · {resetPreview.guideline_signoffs} guideline sign-offs · all uploaded documents &amp; evidence files
              </p>
              <p className="mt-1 text-stone-500" data-testid="factory-reset-kept">
                Keeping {resetPreview.admin_accounts_kept} admin account(s) plus: {resetPreview.kept.join("; ").toLowerCase()}.
              </p>
              <p className="mt-2 font-medium">Type <span className="font-mono font-bold">RESET ALL DATA</span> to confirm:</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="RESET ALL DATA"
                  className="h-9 w-56 rounded-md border border-stone-300 bg-white px-3 font-mono text-sm" data-testid="factory-reset-confirm-input" />
                <Button onClick={doFactoryReset} disabled={resetBusy || resetConfirm !== "RESET ALL DATA"}
                  className="h-9 bg-[#C0392B] text-white hover:bg-[#96281B]" data-testid="factory-reset-execute-button">
                  {resetBusy ? "Wiping…" : "Delete everything"}
                </Button>
                <button onClick={() => { setResetPreview(null); setResetConfirm(""); }} className="text-xs text-stone-500 underline" data-testid="factory-reset-cancel">Cancel</button>
              </div>
            </div>
          )}
          {resetResult && (
            <div className="mt-3 rounded-md bg-stone-50 p-3 text-xs text-stone-600" data-testid="factory-reset-result">
              <p><strong>Done.</strong> {resetResult.staff_accounts_deleted} staff accounts deleted and all operational records wiped. Policies, knowledge base, and the training catalog were kept.</p>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="audit-pack-card">
          <p className="text-sm font-semibold text-stone-800">Downloadable audit packs</p>
          <p className="mt-0.5 text-xs text-stone-400">
            Builds the full pack: one Auditor Book PDF per employee (cover, open-items findings, book index, every document on letter-size pages), grouped into ZIPs of 5 with a manifest CSV, QA report, owner reconciliation PDF and validation summary — same structure as the packs you already have. For one employee, use the "Auditor book (PDF)" button on their employee file.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button onClick={buildPack} disabled={packBusy || pack.state === "running"} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="audit-pack-build">
              {pack.state === "running" ? `Building… ${pack.done}/${pack.total}` : "Build full audit pack"}
            </Button>
            {pack.ready && (
              <Button onClick={downloadPack} variant="outline" className="border-stone-300" data-testid="audit-pack-download">
                Download master ZIP
              </Button>
            )}
            {pack.state === "running" && <span className="text-xs text-stone-500" data-testid="audit-pack-progress">working on: {pack.current}</span>}
            {pack.state === "error" && <span className="text-xs text-[#C0392B]" data-testid="audit-pack-error">{pack.error}</span>}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="seed-baseline-card">
          <p className="text-sm font-semibold text-stone-800">Seed baseline data (fresh environment)</p>
          <p className="mt-0.5 text-xs text-stone-400">Runs the bundled imports from the saved source files: roster (with rehire terms & corrections), face-to-face interviews, and all reference files (email + verbal). No uploads needed — the files ship with the app. Run this ONCE after a factory reset in a fresh environment.</p>
          <div className="mt-3">
            <Button onClick={doSeed} disabled={seedBusy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="seed-baseline-button">
              {seedBusy ? "Seeding… (this takes a minute)" : "Seed baseline data"}
            </Button>
          </div>
          {seedResult && (
            <div className="mt-3 rounded-md bg-stone-50 p-3 text-xs text-stone-600" data-testid="seed-baseline-result">
              <p><strong>{seedResult.ok ? "Seeded." : "Finished with errors."}</strong> Staff accounts: {seedResult.staff_accounts}</p>
              <ul className="mt-1 space-y-1">
                {(seedResult.steps || []).map((s) => (
                  <li key={s.script} className="flex gap-2">
                    <span className={s.ok ? "text-[#1e8449]" : "text-[#C0392B]"}>{s.ok ? "OK" : "FAIL"}</span>
                    <span className="font-mono">{s.script}</span>
                    <span className="truncate text-stone-400">{(s.out || "").split("\n").filter(Boolean).pop()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {isAdmin && visits && visits.total > 0 && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="visit-lookup">
          <p className="text-sm font-semibold text-stone-800">Visit log</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="visit-summary">
            <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600">{visits.total.toLocaleString()} visits</span>
            <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600">{visits.first_date} → {visits.last_date}</span>
            <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600">{visits.clients} clients · {visits.caregivers} caregivers</span>
            <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600">Scheduled {visits.scheduled_hours}h · Worked {visits.worked_hours}h</span>
            {visits.short_visits > 0 && <span className="rounded-full bg-[#B7791F]/10 px-2.5 py-1 text-xs font-semibold text-[#8a5d14]">{visits.short_visits} worked short of schedule</span>}
            {visits.missed_visits > 0 && <span className="rounded-full bg-[#C0392B]/10 px-2.5 py-1 text-xs font-semibold text-[#C0392B]" data-testid="visit-missed-chip">{visits.missed_visits} missed visits</span>}
          </div>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div data-testid="visit-client-list">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Clients in visit log</p>
              <div className="mt-2 max-h-96 space-y-1 overflow-y-auto">
                {(visitClients || []).filter((c) => c.client_name).map((c) => (
                  <button key={c.client_name} onClick={() => openClientLookup(c.client_name)}
                    className={`block w-full rounded-md border px-3 py-2 text-left text-xs transition-colors ${lookup?.client_name === c.client_name ? "border-primary bg-accent" : "border-stone-200 hover:bg-stone-50"}`}
                    data-testid={`visit-client-${c.client_name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <span className="font-medium text-stone-800">{c.client_name}</span>
                    <span className="ml-2 text-stone-400">{c.visits} visits · {c.caregiver_count} caregivers · {c.first_date} → {c.last_date}</span>
                  </button>
                ))}
              </div>
            </div>
            <div data-testid="visit-caregiver-panel">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">
                {lookup ? `Everyone who worked with ${lookup.client_name} (${lookup.total_visits} visits)` : "Pick a client — audit-day reverse lookup"}
              </p>
              {lookupBusy && <p className="mt-2 text-xs text-stone-400">Loading…</p>}
              {lookup && (
                <div className="mt-2 max-h-96 space-y-2 overflow-y-auto">
                  {lookup.items.map((g) => (
                    <div key={g.caregiver_name} className="rounded-md border border-stone-100 p-3" data-testid={`lookup-cg-${g.caregiver_name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        {g.flag_count === 0
                          ? <CheckFat size={14} className="shrink-0 text-[#27AE60]" />
                          : <Warning size={14} className="shrink-0 text-[#C0392B]" />}
                        {g.user_id
                          ? <Link to={`/team/${g.user_id}`} className="text-xs font-semibold text-primary hover:underline">{g.caregiver_name}</Link>
                          : <span className="text-xs font-semibold text-stone-700">{g.caregiver_name}</span>}
                        <span className="text-[10px] text-stone-400">{g.visits} visits · {g.first_date} → {g.last_date} · sched {g.scheduled_hours}h / worked {g.worked_hours}h</span>
                        {g.short_visits > 0 && <span className="rounded bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#8a5d14]">{g.short_visits} short</span>}
                        {g.user_id && (
                          <button type="button" onClick={() => setPdfView({ url: `${api.defaults.baseURL}/users/${g.user_id}/evidence-packet`, title: `Audit packet — ${g.caregiver_name}` })}
                            className="ml-auto text-[10px] font-semibold text-primary underline" data-testid={`lookup-packet-${g.user_id}`}>Audit packet</button>
                        )}
                      </div>
                      {g.flags.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {g.flags.map((f) => (
                            <span key={`${f.label}-${f.detail}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${f.severity === "due" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" : "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"}`}>
                              {f.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="who-worked-card">
          <p className="text-sm font-semibold text-stone-800">Who worked since a date</p>
          <p className="mt-0.5 text-xs text-stone-400">Everyone with at least one visit on or after the date — matched against the roster with active/inactive status. Defaults to Sept 15, 2024.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input type="date" value={sinceDate} onChange={(e) => setSinceDate(e.target.value)}
              className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="who-worked-date" />
            <Button onClick={runWhoWorked} disabled={whoBusy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="who-worked-run">
              {whoBusy ? "Checking…" : "Run report"}
            </Button>
            {whoWorked && (
              <span className="text-xs text-stone-500" data-testid="who-worked-count">
                {whoWorked.count} people worked since {whoWorked.since} · {whoWorked.matched} matched to roster
              </span>
            )}
          </div>
          {whoWorked && whoWorked.items.length > 0 && (
            <div className="mt-3 max-h-96 overflow-y-auto" data-testid="who-worked-results">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-stone-200 text-[10px] font-bold uppercase tracking-wide text-stone-400">
                    <th className="pb-2 pr-3">Caregiver</th>
                    <th className="pb-2 pr-3">Roster status</th>
                    <th className="pb-2 pr-3">Employee ID</th>
                    <th className="pb-2 pr-3">Visits</th>
                    <th className="pb-2 pr-3">Hours</th>
                    <th className="pb-2 pr-3">First → last visit</th>
                    <th className="pb-2">Clients</th>
                  </tr>
                </thead>
                <tbody>
                  {whoWorked.items.map((w) => (
                    <tr key={w.caregiver_name} className="border-b border-stone-100 last:border-0" data-testid={`who-worked-row-${(w.user_id || w.caregiver_name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <td className="py-1.5 pr-3 font-medium text-stone-800">
                        {w.user_id ? <Link to={`/team/${w.user_id}`} className="text-primary hover:underline">{w.caregiver_name}</Link> : w.caregiver_name}
                      </td>
                      <td className="py-1.5 pr-3">
                        {!w.user_id
                          ? <span className="rounded bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#C0392B]">NOT ON ROSTER</span>
                          : w.active
                            ? <span className="rounded bg-[#27AE60]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#1e8449]">ACTIVE</span>
                            : <span className="rounded bg-stone-200 px-1.5 py-0.5 text-[10px] font-bold text-stone-500">INACTIVE</span>}
                      </td>
                      <td className="py-1.5 pr-3 font-mono text-stone-600">{w.employee_id || w.dcw_code || "—"}</td>
                      <td className="py-1.5 pr-3 text-stone-700">{w.visits}</td>
                      <td className="py-1.5 pr-3 text-stone-700">{w.worked_hours}h</td>
                      <td className="py-1.5 pr-3 text-stone-500">{w.first_date} → {w.last_date}</td>
                      <td className="py-1.5 text-stone-500">{w.client_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {whoWorked && whoWorked.items.length === 0 && (
            <p className="mt-3 text-xs text-stone-500" data-testid="who-worked-empty">No visits on or after {whoWorked.since}. Import the visit log first, or pick an earlier date.</p>
          )}
        </div>
      )}

      {isAdmin && showFin && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="financials-card">
          <p className="text-sm font-semibold text-stone-800">Financials — what you're making per client &amp; caregiver</p>
          <p className="mt-0.5 text-xs text-stone-400">Worked hours from the visit log × bill and pay rates. CHC clients bill at the default rate automatically; set other rates on the client record and pay rates on each employee.</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs text-stone-500">Employer burden %
              <input value={burden} onChange={(e) => setBurden(e.target.value)} className="mt-1 block h-8 w-20 rounded-md border border-stone-300 px-2 text-sm" data-testid="fin-burden" />
            </label>
            <label className="text-xs text-stone-500">CHC default bill rate $/hr
              <input value={chcRate} onChange={(e) => setChcRate(e.target.value)} className="mt-1 block h-8 w-24 rounded-md border border-stone-300 px-2 text-sm" data-testid="fin-chc-rate" />
            </label>
            <Button size="sm" variant="outline" onClick={saveFinSettings} className="border-stone-300" data-testid="fin-save-settings">Save</Button>
            <Button size="sm" variant="ghost" onClick={loadFin} disabled={finBusy} className="text-xs" data-testid="fin-refresh">{finBusy ? "Loading…" : "Refresh"}</Button>
            {fin && (
              <span className="text-xs text-stone-500" data-testid="fin-totals">
                Total: {fin.totals.hours.toLocaleString()}h · revenue ${fin.totals.revenue.toLocaleString()} · cost ${fin.totals.cost.toLocaleString()} · <strong className={fin.totals.margin >= 0 ? "text-[#1e8449]" : "text-[#C0392B]"}>margin ${fin.totals.margin.toLocaleString()}</strong>
              </span>
            )}
          </div>
          {fin && (
            <>
              <div className="mt-3 flex gap-2" data-testid="fin-tabs">
                {["clients", "caregivers"].map((t) => (
                  <button key={t} onClick={() => setFinTab(t)} data-testid={`fin-tab-${t}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${finTab === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>
                    {t === "clients" ? `By client (${fin.clients.length})` : `By caregiver (${fin.caregivers.length})`}
                  </button>
                ))}
              </div>
              <div className="mt-2 max-h-80 overflow-y-auto" data-testid="fin-table">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-stone-200 text-[10px] font-bold uppercase tracking-wide text-stone-400">
                      <th className="pb-2 pr-3">{finTab === "clients" ? "Client" : "Caregiver"}</th>
                      <th className="pb-2 pr-3">{finTab === "clients" ? "Bill rate" : "Pay rate"}</th>
                      <th className="pb-2 pr-3">Hours</th>
                      <th className="pb-2 pr-3">Revenue</th>
                      <th className="pb-2 pr-3">Cost</th>
                      <th className="pb-2">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(finTab === "clients" ? fin.clients : fin.caregivers).map((r) => (
                      <tr key={r.client_id || r.user_id} className="border-b border-stone-100 last:border-0" data-testid={`fin-row-${r.client_id || r.user_id}`}>
                        <td className="py-1.5 pr-3 font-medium text-stone-800">
                          {r.client_id
                            ? <Link to={`/clients/${r.client_id}`} className="text-primary hover:underline">{r.client_name}</Link>
                            : <Link to={`/team/${r.user_id}`} className="text-primary hover:underline">{r.name}</Link>}
                        </td>
                        <td className="py-1.5 pr-3 text-stone-600">
                          {finTab === "clients"
                            ? (r.bill_rate ? `$${r.bill_rate}/hr ` : "") + `(${r.rate_source})`
                            : (r.pay_rate ? `$${r.pay_rate}/hr` : <span className="text-[#8a5d14]">not set</span>)}
                        </td>
                        <td className="py-1.5 pr-3 text-stone-600">{r.hours.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-stone-600">${r.revenue.toLocaleString()}</td>
                        <td className="py-1.5 pr-3 text-stone-600">${r.cost.toLocaleString()}</td>
                        <td className={`py-1.5 font-semibold ${r.margin >= 0 ? "text-[#1e8449]" : "text-[#C0392B]"}`}>${r.margin.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="name-key-card">
          <p className="text-sm font-semibold text-stone-800">Name key — masked client names → real names</p>
          <p className="mt-0.5 text-xs text-stone-400">While you build with scrambled client names, keep the who-is-who list here. When you're ready, apply it once: every visit and client record is rewritten to the real name. Two columns — masked name, real name (.xlsx or .csv).</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a href="/uploads" className="text-xs font-medium text-primary underline" data-testid="name-key-uploads-link">Upload the key file on the Uploads page</a>
            {keyInfo && keyInfo.count > 0 && (
              <Button onClick={applyKey} disabled={keyBusy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="name-key-apply">
                {keyBusy ? "Working…" : "Apply real names now"}
              </Button>
            )}
            {keyInfo && (
              <span className="text-xs text-stone-500" data-testid="name-key-count">{keyInfo.count} key entries · {keyInfo.applied} applied</span>
            )}
          </div>
          {keyResult && (
            <div className="mt-3 rounded-md bg-stone-50 p-3 text-xs text-stone-600" data-testid="name-key-result">
              <p><strong>{keyResult.clients_updated}</strong> client records and <strong>{keyResult.visits_updated}</strong> visits now show real names ({keyResult.key_entries_used} key entries used).</p>
              {keyResult.key_entries_no_match?.length > 0 && (
                <p className="mt-1 text-[#8a5d14]">Key entries that matched nothing: {keyResult.key_entries_no_match.join(", ")}</p>
              )}
            </div>
          )}
        </div>
      )}

      {isAdmin && rollup && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="rollup-panel">
          <p className="text-sm font-semibold text-stone-800">Compliance document rollup</p>
          <p className="mt-0.5 text-xs text-stone-400">Every required document/policy treated like a training — its own completion rollup across all active staff. Dated record on file = compliant (amber = artifact still to upload); green ALL CLEAR only when the file is uploaded. Worst items first; click one to see who.</p>
          <div className="mt-4 space-y-2">
            {rollup.map((item) => {
              const unknown = item.unknown || 0;
              const pending = item.pending_artifact || 0;
              const naAll = (item.na || 0) > 0 && (item.clear + item.missing + item.expired + item.due + unknown + pending) === 0;
              const total = item.clear + item.missing + item.expired + item.due + unknown + pending;
              const pct = total ? Math.round(((item.clear + pending) / total) * 100) : 100;
              const flagged = item.missing + item.expired + item.due;
              const open = rollupOpen === item.key;
              const tid = item.key.replace(/[^a-z0-9]+/gi, "-");
              return (
                <div key={item.key} className="rounded-md border border-stone-100" data-testid={`rollup-item-${tid}`}>
                  <div onClick={() => setRollupOpen(open ? "" : item.key)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setRollupOpen(open ? "" : item.key)} className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left" data-testid={`rollup-toggle-${tid}`}>
                    <span className="w-64 shrink-0 truncate text-xs font-medium text-stone-700">{item.label}</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-100">
                      <span className={`block h-full rounded-full ${naAll ? "bg-stone-300" : pct >= 90 ? "bg-[#27AE60]" : pct >= 60 ? "bg-[#B7791F]" : "bg-[#C0392B]"}`} style={{ width: `${naAll ? 0 : pct}%` }} />
                    </span>
                    <span className="w-12 shrink-0 text-right text-xs font-bold text-stone-600" data-testid={`rollup-pct-${tid}`}>{naAll ? "—" : `${pct}%`}</span>
                    {naAll
                      ? <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-bold text-stone-400">N/A</span>
                      : flagged > 0
                        ? <span className="rounded bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#C0392B]" data-testid={`rollup-flagged-${tid}`}>{flagged}</span>
                        : unknown > 0
                          ? <span className="rounded bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#8a5d14]" data-testid={`rollup-unknown-${tid}`}>{unknown} NOT VERIFIED</span>
                          : pending > 0
                            ? <span className="rounded border border-[#B7791F]/40 bg-[#B7791F]/5 px-1.5 py-0.5 text-[10px] font-bold text-[#8a5d14]" data-testid={`rollup-pending-${tid}`}>{pending} FILES TO UPLOAD</span>
                            : <span className="rounded bg-[#27AE60]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#1e8449]">ALL CLEAR</span>}
                    {flagged > 0 && (
                      <button onClick={(e) => { e.stopPropagation(); remindItem(item); }} disabled={remindBusy === item.key}
                        title="Queue reminder emails for everyone flagged (they appear in Notifications for your approval first)"
                        className="rounded border border-primary/40 px-1.5 py-0.5 text-[10px] font-semibold text-accent-foreground hover:bg-accent disabled:opacity-50"
                        data-testid={`rollup-remind-${tid}`}>{remindBusy === item.key ? "…" : "Remind"}</button>
                    )}
                    <span className="text-stone-400">{open ? "▾" : "▸"}</span>
                  </div>
                  {open && item.staff.length > 0 && (
                    <div className="border-t border-stone-100 px-3 py-2" data-testid={`rollup-staff-${tid}`}>
                      <div className="flex flex-wrap gap-1.5">
                        {item.staff.map((s) => (
                          <Link key={s.id} to={`/team/${s.id}`} title={s.detail || s.label}
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium hover:underline ${s.severity === "missing" || s.severity === "expired" ? "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]" : "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"}`}
                            data-testid={`rollup-staff-${tid}-${s.id}`}>
                            {s.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && interviewRecs && interviewRecs.length > 0 && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="interview-records-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-stone-800">Interview records on file ({interviewRecs.length})</p>
            <input value={ivSearch} onChange={(e) => setIvSearch(e.target.value)} placeholder="Search by name…"
              className="h-8 w-56 rounded-md border border-stone-300 bg-white px-3 text-xs" data-testid="interview-records-search" />
          </div>
          <div className="mt-3 max-h-80 space-y-1 overflow-y-auto">
            {interviewRecs.filter((r) => !ivSearch.trim() || r.name.toLowerCase().includes(ivSearch.trim().toLowerCase())).map((r) => (
              <div key={r.record_id} className="flex flex-wrap items-center gap-2 rounded-md border border-stone-100 px-3 py-1.5 text-xs" data-testid={`ivrec-${r.record_id}`}>
                <Link to={`/team/${r.user_id}`} className="font-semibold text-primary hover:underline" data-testid={`ivrec-link-${r.record_id}`}>{r.name}</Link>
                <span className="text-stone-400">{r.record_date}{r.interviewer ? ` · by ${r.interviewer}` : ""}</span>
                {r.provisional && <span className="rounded bg-[#B7791F]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#8a5d14]">PROVISIONAL</span>}
                {r.form_only && <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-500">form record</span>}
                <button type="button" onClick={() => setPdfView({ url: `${api.defaults.baseURL}/users/${r.user_id}/employment-records/${r.record_id}`, title: `Interview — ${r.name}` })}
                  className="ml-auto font-medium text-primary hover:underline" data-testid={`ivrec-view-${r.record_id}`}>View PDF</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && readiness && (
        <div className="mt-8 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="audit-readiness">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-stone-800">Staff readiness — anything missing, flagged</p>
              <p className="mt-0.5 text-xs text-stone-400">Every active caregiver checked against the full surveyor list: interview proof, ID, SS card, I-9, residency, PA/FBI background checks, TB baseline + annual, competency exam, unsigned policies, provisional-hire observation.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-stone-500">
              <input type="checkbox" checked={showClear} onChange={(e) => setShowClear(e.target.checked)} data-testid="audit-show-clear" />
              Show clear staff
            </label>
          </div>
          <div className="mt-4 space-y-2">
            {readiness.items.filter((s) => showClear || s.flag_count > 0).map((s) => (
              <div key={s.id || s.email} className="rounded-md border border-stone-100 px-3 py-2" data-testid={`readiness-${s.id || s.email}`}>
                <div className="flex items-center gap-2">
                  {s.flag_count === 0
                    ? <CheckFat size={15} className="shrink-0 text-[#27AE60]" />
                    : <Warning size={15} className="shrink-0 text-[#C0392B]" />}
                  <Link to={`/team/${s.id}`} className="text-xs font-semibold text-primary hover:underline" data-testid={`readiness-link-${s.id || s.email}`}>{s.name}</Link>
                  {s.flag_count === 0
                    ? <span className="rounded bg-[#27AE60]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#1e8449]">CLEAR</span>
                    : <span className="rounded bg-[#C0392B]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#C0392B]" data-testid={`readiness-count-${s.id || s.email}`}>{s.flag_count} FLAG{s.flag_count > 1 ? "S" : ""}</span>}
                </div>
                {s.flags.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {s.flags.map((f) => (
                      <span key={`${f.label}-${f.detail}`} title={f.detail}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${f.severity === "due" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]" : "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"}`}
                        data-testid={`readiness-flag-${s.id || s.email}-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {f.label}{f.detail ? ` — ${f.detail}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-start gap-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4" data-testid="audit-confirm-note">
        <CheckCircle size={18} className="mt-0.5 shrink-0 text-[#8a5d14]" />
        <p className="text-xs leading-relaxed text-stone-600">
          <strong className="text-[#8a5d14]">Matched to your real HHAeXchange export:</strong> upload the full file (all ~78,000 visits) in one go.
          The first run is always a preview — check the date range, matched counts, and unmatched names — then uncheck &ldquo;Preview only&rdquo; to commit.
          Re-importing the same file is safe: Visit IDs are used to skip duplicates.
        </p>
      </div>
      <PdfViewerModal url={pdfView?.url} title={pdfView?.title || ""} onClose={() => setPdfView(null)} />
    </div>
  );
}
